import axios from 'axios';

import API, { apiBase } from './apiconfig';

import { apiLoadingStart, apiLoadingEnd } from './apiLoadingBus';

import { clearMemberStorage } from './memberStorage';

import {
  isAccessTokenExpiringSoon,
  shouldRefreshAccessToken,
} from './tokenSession';

import {
  acquireCrossTabRefreshLock,
  notifyMemberSessionExpired,
  releaseCrossTabRefreshLock,
  waitForCrossTabRefresh,
} from './sessionSync';

const BASE = apiBase;

const api = axios.create({ baseURL: BASE });

const ADMIN_URL_PATTERN = /\/admin\/|\/v2\/admin\//;

/** Failed login/register must not trigger global session-expired handling. */
const AUTH_ENTRY_URL_PATTERN = /\/(login|register)\/?$/;

/** Single in-flight refresh so parallel 401s share one token/refresh round-trip. */
let memberRefreshPromise = null;
let adminRefreshPromise = null;

function isAdminApiRequest(config) {
  const url = config?.url || '';
  return ADMIN_URL_PATTERN.test(url);
}

function getMemberTokens() {
  return {
    access: localStorage.getItem('access_token'),
    refresh: localStorage.getItem('refresh_token'),
  };
}

function getAdminTokens() {
  return {
    access: localStorage.getItem('admin_access_token'),
    refresh: localStorage.getItem('admin_refresh_token'),
  };
}

function getTokens(adminRequest) {
  return adminRequest ? getAdminTokens() : getMemberTokens();
}

function clearMemberTokens() {
  clearMemberStorage();
}

function clearAdminTokens() {
  ['admin_access_token', 'admin_refresh_token', 'admin_user'].forEach((key) =>
    localStorage.removeItem(key)
  );
}

function persistRefreshedTokens(adminRequest, data) {
  const access = data?.access;
  const refresh = data?.refresh;
  if (!access) return;

  if (adminRequest) {
    localStorage.setItem('admin_access_token', access);
    if (refresh) {
      localStorage.setItem('admin_refresh_token', refresh);
    }
  } else {
    localStorage.setItem('access_token', access);
    if (refresh) {
      localStorage.setItem('refresh_token', refresh);
    }
  }
}

function isRefreshTokenRejected(err) {
  const status = err?.response?.status;
  // 404 usually means the wrong service is bound to the API port (not this backend).
  return status === 401 || status === 403 || status === 404;
}

function isTransientRefreshError(err) {
  return !err?.response;
}

async function postTokenRefresh(adminRequest, refresh) {
  const res = await axios.post(API.TOKEN_REFRESH, { refresh });
  const access = res.data?.access;
  if (!access) {
    throw new Error('Refresh response missing access token');
  }
  persistRefreshedTokens(adminRequest, res.data);
  return access;
}

async function requestTokenRefresh(adminRequest) {
  const slot = adminRequest ? adminRefreshPromise : memberRefreshPromise;
  if (slot) return slot;

  const promise = (async () => {
    const syncedAccess = await waitForCrossTabRefresh(adminRequest, () =>
      getTokens(adminRequest)
    );
    if (syncedAccess) {
      return syncedAccess;
    }

    const { refresh, access } = getTokens(adminRequest);
    if (!refresh) {
      throw new Error('No refresh token');
    }

    if (access && !shouldRefreshAccessToken(access)) {
      return access;
    }

    acquireCrossTabRefreshLock(adminRequest);
    try {
      return await postTokenRefresh(adminRequest, refresh);
    } finally {
      releaseCrossTabRefreshLock(adminRequest);
    }
  })();

  if (adminRequest) {
    adminRefreshPromise = promise;
  } else {
    memberRefreshPromise = promise;
  }

  try {
    return await promise;
  } finally {
    if (adminRequest) {
      adminRefreshPromise = null;
    } else {
      memberRefreshPromise = null;
    }
  }
}

/** Proactively refresh member access token before expiry (keep-alive). */
export async function refreshMemberSessionIfNeeded() {
  const { access, refresh } = getMemberTokens();
  if (!refresh) return null;

  if (access && !shouldRefreshAccessToken(access)) {
    return access;
  }

  try {
    return await requestTokenRefresh(false);
  } catch (err) {
    if (isTransientRefreshError(err)) {
      return access || null;
    }
    return null;
  }
}

export async function refreshMemberSession() {
  const { refresh } = getMemberTokens();
  if (!refresh) return null;
  try {
    return await requestTokenRefresh(false);
  } catch (err) {
    if (!isTransientRefreshError(err) && isRefreshTokenRejected(err)) {
      handleMemberSessionExpired();
    }
    return null;
  }
}

function handleMemberSessionExpired() {
  clearMemberTokens();
  notifyMemberSessionExpired();
}

function tryRetryWithFreshAccess(config, adminRequest) {
  const { access } = getTokens(adminRequest);
  if (access && !isAccessTokenExpiringSoon(access)) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${access}`;
    config._authRetry = true;
    return api.request(config);
  }
  return null;
}

api.interceptors.request.use(async (config) => {
  if (!config.skipGlobalLoading && !config._authRetry) {
    apiLoadingStart();
  }

  const adminRequest = isAdminApiRequest(config);

  if (
    !adminRequest &&
    !config._authRetry &&
    !String(config.url || '').includes('token/refresh')
  ) {
    await refreshMemberSessionIfNeeded();
  }

  const token = getTokens(adminRequest).access;

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  } else if (config.headers) {
    delete config.headers.Authorization;
  }

  return config;
});

api.interceptors.response.use(
  (res) => {
    if (!res.config?.skipGlobalLoading) {
      apiLoadingEnd();
    }
    return res;
  },
  async (err) => {
    const config = err.config || {};
    const skipLoading = config.skipGlobalLoading;
    const is401 = err.response?.status === 401;

    if (is401 && !config._authRetry) {
      const adminRequest = isAdminApiRequest(config);
      const requestUrl = String(config.url || '');
      const isAuthEntry = AUTH_ENTRY_URL_PATTERN.test(requestUrl);

      if (isAuthEntry) {
        if (!skipLoading) {
          apiLoadingEnd();
        }
        return Promise.reject(err);
      }

      const { refresh } = getTokens(adminRequest);

      if (refresh) {
        try {
          const access = await requestTokenRefresh(adminRequest);
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${access}`;
          config._authRetry = true;
          return api.request(config);
        } catch (refreshErr) {
          if (!skipLoading) {
            apiLoadingEnd();
          }

          if (isTransientRefreshError(refreshErr)) {
            return Promise.reject(err);
          }

          const retryRes = tryRetryWithFreshAccess(config, adminRequest);
          if (retryRes) {
            return retryRes;
          }

          if (isRefreshTokenRejected(refreshErr)) {
            if (adminRequest) {
              clearAdminTokens();
            } else {
              handleMemberSessionExpired();
            }
          }

          return Promise.reject(refreshErr);
        }
      } else if (!adminRequest) {
        handleMemberSessionExpired();
      }
    }

    if (!skipLoading) {
      apiLoadingEnd();
    }

    return Promise.reject(err);
  }
);

/**
 * Normalize list API responses: plain array OR paginated { results, items, plans, ... }.
 */
export function asList(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.plans)) return data.plans;
    if (Array.isArray(data.referrals)) return data.referrals;
    if (Array.isArray(data.transactions)) return data.transactions;
    if (Array.isArray(data.data)) return data.data;
  }
  return [];
}

/** asList + optional max length (e.g. dashboard preview rows). */
export function sliceList(data, limit) {
  const list = asList(data);
  if (limit == null || limit < 0) return list;
  return list.slice(0, limit);
}

export default api;
